import React, { useState, useEffect, useRef } from 'react'
import FullCalendar from '@fullcalendar/react'
import { CalendarApi, EventInput, DateSelectArg, EventClickArg, EventApi } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import rrulePlugin from '@fullcalendar/rrule'
import 'bootstrap/dist/css/bootstrap.min.css'
import './Event.scss'
// import gridListImg from '../../../../Assets/Images/gridListView.png'
import { SearchSvg } from 'Components/Svgs'
import listSvgImg from '../../../../Assets/Icons/list-check-svgrepo-com.png'
import hamburgerSvgImg from '../../../../Assets/Icons/squared-hamburger-menu.png'
import Trash from '../../../../Assets/Images/trash-white.png'
import EditIcon from 'Assets/Icons/edit-2.png'
import { ModalWrapper } from 'Components'
import EventsComp from '../../Components/Events/Events'
import eventService from 'Services/eventService'
import { Logger } from 'Helpers/Logger'
import Swal from 'sweetalert2'
import { useLocation } from 'react-router-dom'

const VIEW_MAP: Record<string, string> = {
  dayGridMonth: 'Month',
  timeGridWeek: 'Week',
  listMonth: 'List',
}

const Event: React.FC = () => {
  const location = useLocation()
  const isAdminRoute = location.pathname.includes('/admin/events')
  const [events, setEvents] = useState<EventInput[]>([])
  const [currentTitle, setCurrentTitle] = useState<string>('')
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')
  const [calendarViewName, setCalendarViewName] = useState<'dayGridMonth' | 'dayGridWeek'>(
    'dayGridMonth',
  )
  const calendarRef = useRef<InstanceType<typeof FullCalendar> | null>(null)
  const [loading, setLoading] = useState(false)
  const [currentDate, setCurrentDate] = useState<Date>(new Date())
  const [groupedEvents, setGroupedEvents] = useState<Record<string, EventApi[]>>({})
  const [showModal, setShowModal] = useState(false)
  const [eventToEdit, setEventToEdit] = useState<EventApi | null>(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [showSearch, setShowSearch] = useState(false)

  const transformEvents = (rawEvents: any[]): EventInput[] =>
    rawEvents.map((event) => {
      const base: EventInput = {
        id: event.eventDisplayId,
        title: event.title,
        start: new Date(event.startDate),
        end: new Date(event.endDate),
        extendedProps: {
          description: event.description,
          location: event.location,
          roles: event.roles,
          frequency: event.frequency,
          isRecurring: event.isRecurring,
          eventType: event.eventType,
        },
      }
      if (event.isRecurring) {
        return {
          ...base,
          rrule: { freq: event.frequency?.toUpperCase(), dtstart: event.startDate },
        }
      }
      return base
    })

  const getProducts = async () => {
    setLoading(true)
    try {
      const response = await eventService.index()
      setEvents(transformEvents(response?.data?.data || []))
    } catch (error: any) {
      Logger('response error', error?.response)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getProducts()
  }, [])

  const getCalendarApi = () => calendarRef.current?.getApi()

  const updateGroupedEvents = () => {
    setTimeout(() => {
      const api = getCalendarApi()
      if (!api) return

      const view = api.view
      const allEvents = api.getEvents() as EventApi[]
      const inView = allEvents.filter(
        (e) => e.start && e.start >= view.currentStart && e.start < view.currentEnd,
      )
      const grouped = inView.reduce<Record<string, EventApi[]>>((acc, ev) => {
        const date = ev.start!.toISOString().split('T')[0]
        if (!acc[date]) acc[date] = []
        acc[date].push(ev)

        return acc
      }, {})

      setGroupedEvents(grouped)
    }, 0)
  }

  const handlePrev = () => {
    getCalendarApi()?.prev()
  }

  const handleNext = () => {
    getCalendarApi()?.next()
  }

  const handleToday = () => {
    getCalendarApi()?.today()
  }

  const handleViewToggle = (mode: 'calendar' | 'list') => {
    const api = getCalendarApi()
    if (!api) return
    setViewMode(mode)

    if (mode === 'calendar') {
      api.changeView(calendarViewName)
    }
  }

  const handleSelectViewChange = (value: string) => {
    const api = getCalendarApi()
    if (!api) return

    const name = value === 'Week' ? 'dayGridWeek' : 'dayGridMonth'
    setCalendarViewName(name as 'dayGridMonth' | 'dayGridWeek')
    setViewMode('calendar')
    api.changeView(name)
  }

  // const handleDatesSet = (arg: any) => {
  //   setCurrentDate(arg.start)
  //   setCurrentTitle(arg.view.title)
  //   updateGroupedEvents()
  // }

  const handleEventsSet = (events: EventApi[]) => {
    const api = calendarRef.current?.getApi()
    if (!api) return

    const view = api.view
    const eventsInView = events.filter((event) => {
      return event.start && event.start >= view.currentStart && event.start < view.currentEnd
    })

    const grouped = eventsInView.reduce(
      (acc, event) => {
        const date = event.start!.toLocaleDateString('en-CA')
        if (!acc[date]) acc[date] = []
        acc[date].push(event)

        return acc
      },
      {} as Record<string, EventApi[]>,
    )

    setCurrentDate(view.start)
    setCurrentTitle(view.title)
    setGroupedEvents(grouped)
  }

  // const handleDateClick = (info: DateSelectArg) => {
  //   const title = prompt('Enter event title:')
  //   if (title) {
  //     setEvents((prev) => [
  //       ...prev,
  //       { title, start: info.startStr, end: info.endStr, allDay: info.allDay },
  //     ])
  //   }
  // }

  // const handleEventClick = (info: EventClickArg) => {
  //   alert(`Event: ${info.event.title}\nDate: ${info.event.start?.toLocaleString()}`)
  // }

  const toggleModal = () => {
    setShowModal((prev) => !prev)
    if (showModal) setEventToEdit(null)
  }

  const openCreateModal = () => {
    setEventToEdit(null)
    setShowModal(true)
  }

  const openEditModal = (event: EventApi) => {
    setEventToEdit(event)
    setShowModal(true)
  }

  const handleDelete = async (event: EventApi) => {
    const result = await Swal.fire({
      title: 'Delete event?',
      text: `${event.title} will be permanently removed.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      customClass: {
        confirmButton: 'btn btn-default me-3',
        cancelButton: 'btn btn-outline-secondary',
      },
      buttonsStyling: false,
    })
    if (!result.isConfirmed) return
    try {
      const res = await eventService.deleteEvent(event?.id)
      // console.log('r', res.data)
      if (res.data.status === true) {
        await getProducts()
        Swal.fire({ icon: 'success', text: 'Event deleted successfully', confirmButtonText: 'Ok' })
      } else {
        showErrorAlert(res.data.message || 'Something went wrong!')
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        text: (error?.response?.data?.error || error?.response?.data?.message) ?? 'Delete failed',
        confirmButtonText: 'Ok',
      })
    }
  }

  const q = searchQuery.trim().toLowerCase()

  const filteredGrouped = q
    ? Object.fromEntries(
        Object.entries(groupedEvents)
          .map(([date, evs]) => [
            date,
            evs.filter(
              (ev) =>
                ev.title?.toLowerCase().includes(q) ||
                ev.extendedProps?.description?.toLowerCase().includes(q) ||
                ev.extendedProps?.location?.toLowerCase().includes(q),
            ),
          ])
          .filter(([, evs]) => evs.length > 0),
      )
    : groupedEvents

  const showErrorAlert = (text: string) => {
    Swal.mixin({
      customClass: { cancelButton: 'btn btn-danger' },
      buttonsStyling: false,
    }).fire({
      title: 'Oops!',
      text,
      icon: 'warning',
      showCancelButton: true,
      showConfirmButton: false,
      cancelButtonText: 'Close',
      reverseButtons: true,
    })
  }

  return (
    <div className='calendar-page container py-4'>
      <div className='calendar-header d-flex justify-content-between align-items-center mb-4'>
        <div className='d-flex align-items-center gap-2'>
          <button className='btn btn-outline-secondary btn-sm today' onClick={handleToday}>
            Today
          </button>
          <div className='calendarTitle'>
            <button className='btn btn-light btn-sm' onClick={handlePrev}>
              &lt;
            </button>
            <span className='fw-semibold mx-2 w-50'>{currentTitle}</span>
            <button className='btn btn-light btn-sm' onClick={handleNext}>
              &gt;
            </button>
          </div>
        </div>

        <div className='d-flex align-items-center gap-2'>
          <div className='d-flex align-items-center gap-1'>
            <button
              className='btn btn-light btn-sm'
              onClick={() => {
                setShowSearch((s) => !s)
                setSearchQuery('')
              }}
              title='Search events'
            >
              <SearchSvg />
            </button>
            {showSearch && (
              <input
                autoFocus
                type='text'
                className='form-control form-control-sm'
                placeholder='Search by title, location…'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: 200 }}
              />
            )}
          </div>

          <select
            className='form-select form-select-sm selection-dropdown'
            value={VIEW_MAP[calendarViewName] ?? 'Month'}
            onChange={(e) => handleSelectViewChange(e.target.value)}
          >
            <option value='Month'>Month</option>
            <option value='Week'>Week</option>
          </select>

          <div className='calender-switch'>
            <button
              onClick={() => handleViewToggle('calendar')}
              className={`dayGridView ${viewMode === 'calendar' ? 'active' : 'inactive'}`}
              title='Calendar View'
            >
              <img src={hamburgerSvgImg} alt='calendar icon' width='18' height='18' />
            </button>
            <button
              onClick={() => handleViewToggle('list')}
              className={`listView ${viewMode === 'list' ? 'active' : 'inactive'}`}
              title='List View'
            >
              <img src={listSvgImg} alt='list icon' width='18' height='18' />
            </button>
          </div>

          {isAdminRoute && (
            <button className='btn btn-pink btn-sm new-event' onClick={openCreateModal}>
              <i className='bi bi-plus-lg me-1'></i> New Event
            </button>
          )}
        </div>
      </div>

      <div
        className='calendar-container rounded-4 p-4 bg-white'
        style={{ display: viewMode === 'calendar' ? 'block' : 'none' }}
      >
        {loading && (
          <div className='text-center py-3'>
            <span className='spinner-border spinner-border-sm' />
          </div>
        )}
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin, rrulePlugin]}
          initialView='dayGridMonth'
          headerToolbar={false}
          height='auto'
          selectable
          editable
          selectMirror
          events={events}
          eventsSet={handleEventsSet}
          // select={handleDateClick}
          // eventClick={handleEventClick}
          // datesSet={handleDatesSet}
          eventTimeFormat={{ hour: 'numeric', meridiem: 'short', hour12: true }}
          eventContent={(arg: any) => (
            <div className='custom-event'>
              <ul className='custom-event-list'>
                <li title={arg.event.title}>
                  <strong>{arg.timeText}</strong> - {arg.event.title}
                </li>
              </ul>
            </div>
          )}
        />
      </div>

      {viewMode === 'list' && (
        <div className='event-list-view'>
          <div className='container'>
            <h3>Upcoming Events</h3>

            {q && !Object.keys(filteredGrouped).length && (
              <div className='text-center text-muted'>No events matching {searchQuery}</div>
            )}

            {!q && !Object.keys(filteredGrouped).length && (
              <div className='text-center text-muted'>No events this period</div>
            )}

            {(Object.entries(filteredGrouped) as [string, EventApi[]][])
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([date, evs]) => (
                <div key={date} className='event-day-group'>
                  {evs.map((ev, index) => {
                    const time = ev.start?.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })

                    return (
                      <div key={ev.id}>
                        <div className='event-day-box d-flex justify-content-between align-items-center'>
                          <div className='event-day'>
                            <div
                              className='event-day-title'
                              style={{ visibility: index === 0 ? 'visible' : 'hidden' }}
                            >
                              {new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                                day: 'numeric',
                              })}
                            </div>

                            <ul className='event-list'>
                              <li className='event-item'>
                                <span>{time} -</span> {ev.title}
                                {/* {ev.extendedProps?.location && (
                                  <span className='event-location text-muted ms-2 small'>
                                    {ev.extendedProps.location}
                                  </span>
                                )}
                                {ev.extendedProps?.isRecurring && (
                                  <span className='badge bg-light text-secondary ms-2 small'>
                                    {ev.extendedProps.frequency}
                                  </span>
                                )} */}
                              </li>
                            </ul>
                          </div>

                          <div className='d-flex align-items-center gap-1 position-relative'>
                            {isAdminRoute && (
                              <>
                                <button
                                  className='btn ms-0 ps-0 border-0 dropdown-toggl'
                                  title='Edit event'
                                  onClick={() => openEditModal(ev)}
                                >
                                  <img src={EditIcon} alt='edit' />
                                </button>
                                <button
                                  className='btn ms-0 ps-0 border-0 dropdown-toggl'
                                  title='Delete event'
                                  onClick={() => handleDelete(ev)}
                                >
                                  <img src={Trash} alt='delete' />
                                </button>{' '}
                              </>
                            )}

                            <button className='btn ms-0 ps-0 border-0 dropdown-toggl'>
                              <svg width='18' height='18' viewBox='0 0 18 18' fill='none'>
                                <path
                                  d='M7.49991 8.4705C7.49991 7.72933 8.0955 7.14697 8.82344 7.14697C9.55138 7.14697 10.147 7.74256 10.147 8.4705C10.147 9.19844 9.55138 9.79403 8.82344 9.79403C8.0955 9.79403 7.49991 9.21168 7.49991 8.4705Z'
                                  fill='#707070'
                                />
                                <path
                                  d='M7.49991 14.1175C7.49991 13.3763 8.0955 12.7939 8.82344 12.7939C9.55138 12.7939 10.147 13.3895 10.147 14.1175C10.147 14.8454 9.55138 15.441 8.82344 15.441C8.0955 15.441 7.49991 14.8587 7.49991 14.1175Z'
                                  fill='#707070'
                                />
                                <path
                                  d='M7.49991 2.82353C7.49991 2.08235 8.0955 1.5 8.82344 1.5C9.55138 1.5 10.147 2.09559 10.147 2.82353C10.147 3.55147 9.55138 4.14706 8.82344 4.14706C8.0955 4.14706 7.49991 3.56471 7.49991 2.82353Z'
                                  fill='#707070'
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ))}
          </div>
        </div>
      )}

      <ModalWrapper
        showModal={showModal}
        closeModal={toggleModal}
        title={eventToEdit ? 'Edit Event' : 'Create New Event'}
      >
        <EventsComp
          eventToEdit={eventToEdit}
          onSuccess={() => {
            toggleModal()
            getProducts()
          }}
        />
      </ModalWrapper>
    </div>
  )
}

export default Event
